export const FLEX_EMULATOR_SESSION_KEY = "m360.flexEmulatorSession";
export const FLEX_EMULATOR_URL_PARAM = "flexEmulator";
export const FLEX_EMULATOR_SESSION_CHANGED_EVENT = "m360:flexEmulatorSessionChanged";

/**
 * Whether this tab is in an emulator session: a DEV-build-only state, entered
 * by opening an emulator console deep-link (`?flexEmulator=true`), that turns
 * on Flex call UI and transcript summaries without Flagr targeting. Always
 * false in production builds.
 */
export const isFlexEmulatorSession = (): boolean => {
  if (!import.meta.env.DEV) return false;

  try {
    return window.sessionStorage.getItem(FLEX_EMULATOR_SESSION_KEY) === "true";
  } catch {
    return false;
  }
};

export const hasFlexEmulatorParam = (search: string): boolean => {
  if (!import.meta.env.DEV) return false;

  try {
    return new URLSearchParams(search).get(FLEX_EMULATOR_URL_PARAM) === "true";
  } catch {
    return false;
  }
};

/** Persists the session when the URL carries the emulator param. Returns whether a session is now active. */
export const captureFlexEmulatorSession = (search: string): boolean => {
  if (hasFlexEmulatorParam(search)) {
    try {
      window.sessionStorage.setItem(FLEX_EMULATOR_SESSION_KEY, "true");
    } catch {
      // Storage unavailable; the session lasts for this URL only.
    }
    return true;
  }

  return isFlexEmulatorSession();
};

export const withFlexEmulatorParam = (path: string): string => {
  const queryIndex = path.indexOf("?");
  if (queryIndex >= 0) {
    try {
      if (new URLSearchParams(path.slice(queryIndex + 1)).has(FLEX_EMULATOR_URL_PARAM)) return path;
    } catch {
      // Fall through and append; malformed search strings fail safe below.
    }
  }
  const separator = queryIndex >= 0 ? "&" : "?";
  return `${path}${separator}${FLEX_EMULATOR_URL_PARAM}=true`;
};

/** Agent-flow deep-links stay on the current page (like the real Flex plugin) instead of bouncing through `/`. */
export const incomingCallPathFor = (pathname: string, phone: string | null, callSid: string): string =>
  withFlexEmulatorParam(
    `${pathname}?activeCall=true&phone=${encodeURIComponent(phone ?? "")}&callSid=${encodeURIComponent(callSid)}`
  );

export const wrapUpPathFor = (pathname: string, callSid: string): string =>
  withFlexEmulatorParam(`${pathname}?activeCallEnded=true&callSid=${encodeURIComponent(callSid)}`);

/** Explicitly switches the session on/off (avatar menu). Notifies live hook instances. */
export const setFlexEmulatorSessionActive = (active: boolean): void => {
  if (!import.meta.env.DEV) return;

  try {
    if (active) {
      window.sessionStorage.setItem(FLEX_EMULATOR_SESSION_KEY, "true");
    } else {
      window.sessionStorage.removeItem(FLEX_EMULATOR_SESSION_KEY);
      const url = new URL(window.location.href);
      if (url.searchParams.has(FLEX_EMULATOR_URL_PARAM)) {
        url.searchParams.delete(FLEX_EMULATOR_URL_PARAM);
        window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      }
    }
  } catch {
    // Storage unavailable; nothing to persist.
  }

  try {
    window.dispatchEvent(new Event(FLEX_EMULATOR_SESSION_CHANGED_EVENT));
  } catch {
    // Non-DOM environment; nothing to notify.
  }
};
